import { useState, useMemo } from "react";
import { StockPricePoint } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  priceHistory: StockPricePoint[];
  currentPrice: number;
  prevClose: number;
}

type Range = "1D" | "1W" | "1M";

export default function StockChart({ priceHistory, currentPrice, prevClose }: Props) {
  const { isDark } = useTheme();
  const [range, setRange] = useState<Range>("1M");

  const filtered = useMemo(() => {
    const now = Date.now();
    const cutoff: Record<Range, number> = {
      "1D": now - 24 * 3600000,
      "1W": now - 7 * 24 * 3600000,
      "1M": now - 30 * 24 * 3600000,
    };
    return priceHistory.filter((p) => new Date(p.time).getTime() >= cutoff[range]);
  }, [priceHistory, range]);

  const isUp = currentPrice >= prevClose;
  const changeAmt = currentPrice - prevClose;
  const changePct = prevClose > 0 ? (changeAmt / prevClose) * 100 : 0;

  const minPrice = Math.min(...filtered.map((p) => p.price));
  const maxPrice = Math.max(...filtered.map((p) => p.price));
  const priceRange = maxPrice - minPrice || 1;

  const W = 800;
  const H = 200;
  const PAD = 10;

  const points = filtered.map((p, i) => {
    const x = PAD + (i / Math.max(filtered.length - 1, 1)) * (W - PAD * 2);
    const y = H - PAD - ((p.price - minPrice) / priceRange) * (H - PAD * 2);
    return `${x},${y}`;
  });

  const lineColor = isUp ? "#34d399" : "#f87171";
  const fillId = isUp ? "fillUp" : "fillDown";
  const fillStart = isUp ? "#34d39930" : "#f8717130";
  const fillEnd = isUp ? "#34d39900" : "#f8717100";

  const pathD = points.length > 1
    ? `M ${points.join(" L ")}`
    : "";

  const areaD = points.length > 1
    ? `M ${PAD},${H - PAD} L ${points.join(" L ")} L ${W - PAD},${H - PAD} Z`
    : "";

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`rounded-2xl border p-5 ${cardBg}`}>
      {/* Price Header */}
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div>
          <p className={`text-3xl font-black ${textPrimary}`}>
            {currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <span className={`text-base font-medium ml-1 ${textSecondary}`}>coins</span>
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-sm font-bold ${isUp ? "text-emerald-400" : "text-red-400"}`}>
              {isUp ? "+" : ""}{changeAmt.toFixed(2)} ({isUp ? "+" : ""}{changePct.toFixed(2)}%)
            </span>
            <span className={`text-xs ${textSecondary}`}>前日比</span>
          </div>
        </div>
        <div className="ml-auto flex gap-1">
          {(["1D", "1W", "1M"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                range === r
                  ? isUp
                    ? "bg-emerald-400/20 text-emerald-400"
                    : "bg-red-400/20 text-red-400"
                  : isDark
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* SVG Chart */}
      <div className="w-full overflow-hidden rounded-xl">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: "180px" }}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={fillStart} />
              <stop offset="100%" stopColor={fillEnd} />
            </linearGradient>
          </defs>
          {areaD && (
            <path d={areaD} fill={`url(#${fillId})`} />
          )}
          {pathD && (
            <path d={pathD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" />
          )}
          {/* Current price line */}
          {filtered.length > 0 && (() => {
            const lastPt = filtered[filtered.length - 1];
            const y = H - PAD - ((lastPt.price - minPrice) / priceRange) * (H - PAD * 2);
            return (
              <line
                x1={PAD}
                y1={y}
                x2={W - PAD}
                y2={y}
                stroke={lineColor}
                strokeWidth="0.5"
                strokeDasharray="4,4"
                opacity="0.5"
              />
            );
          })()}
        </svg>
      </div>

      {/* Price Range */}
      <div className={`flex justify-between mt-2 text-xs ${textSecondary}`}>
        <span>安値: {minPrice.toFixed(2)}</span>
        <span>高値: {maxPrice.toFixed(2)}</span>
      </div>
    </div>
  );
}
